import { MIME_TO_EXTENSION } from '@vindr/services/constants/common.constant';

export async function blobToFile(
  blob: Blob | string,
  fileName: string
): Promise<File> {
  return await fetch(blob as string)
    .then((res) => res.blob())
    .then((blob) => new File([blob], fileName, { type: blob.type }));
}

export function fileToBlob(file: File): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsArrayBuffer(file);

    reader.onload = (e) => {
      resolve(e.target.result as unknown as Blob);
    };

    reader.onerror = reject;
  });
}

export function fileToBlob2(file: File) {
  return new Blob([file], { type: file.type });
}

export function fileToBase64(file: File): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = (event) => {
      const base64String = event.target.result as string;
      resolve(base64String);
    };

    reader.onerror = (error) => {
      reject(error);
    };
  });
}

export async function fileToUint8Array(file: File): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      const result = event.target?.result;
      if (result instanceof ArrayBuffer) {
        const uint8Array = new Uint8Array(result);
        resolve(uint8Array);
      } else {
        reject(new Error('Failed to read file as ArrayBuffer'));
      }
    };

    reader.onerror = () => {
      reject(reader.error);
    };

    reader.readAsArrayBuffer(file);
  });
}

export function uint8ArrayToFile(
  uint8Array: Uint8Array,
  fileName: string,
  mimeType: string
): File {
  const blob = new Blob([uint8Array], { type: mimeType });
  return new File([blob], fileName);
}

export function blobToFile2(blob: Blob, fileName: string) {
  return new File([blob], fileName);
}

export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);

    reader.onload = (event) => {
      const base64String = event.target.result as string;
      resolve(base64String);
    };

    reader.onerror = reject;
  });
}

export function blobToURL(obj: Blob | MediaSource): string {
  return URL.createObjectURL(obj);
}

const base64Decoder = (base64: string) => {
  if (!base64) return null;
  const arr = base64.split(',');
  const type = arr[0].match(/:(.*?);/)?.[1];
  const bstr = window.atob(arr[1]);
  const n = bstr.length;
  const u8arr = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    u8arr[i] = bstr.charCodeAt(i);
  }
  return {
    u8arr,
    type,
  };
};

export const base64ToBlob = (base64: string) => {
  const { u8arr, type } = base64Decoder(base64);
  if (!u8arr || !type) return null;
  return new Blob([u8arr], { type });
};

export const base64ToFile = (base64: string, fileName: string) => {
  const { u8arr, type } = base64Decoder(base64);
  if (!u8arr || !type) return null;
  return new File([u8arr], fileName, { type });
};

export function stringToEventCode(str: string) {
  if (!str.includes('+')) return { key: str };

  const modifier = str.split('+').map((it) => it.trim());
  const key = modifier.splice(-1)[0].trim();

  let [ctrlKey, shiftKey, altKey, metaKey] = [false, false, false, false];

  const metaCheck = ['⌘', '⊞', 'win', 'meta', 'cmd', 'command'];
  const ctrlCheck = ['ctrl', 'control', '⌃'];
  const altCheck = ['alt', 'option', '⌥'];
  const shiftCheck = ['shift', '⇧'];

  const [metaRegex, ctrlRegex, altRegex, shiftRegex] = [
    new RegExp(metaCheck.join('|'), 'ig'),
    new RegExp(ctrlCheck.join('|'), 'ig'),
    new RegExp(altCheck.join('|'), 'ig'),
    new RegExp(shiftCheck.join('|'), 'ig'),
  ];

  modifier.forEach((it) => {
    [ctrlKey, shiftKey, altKey, metaKey] = [
      ctrlRegex.test(it),
      shiftRegex.test(it),
      altRegex.test(it),
      metaRegex.test(it),
    ];
  });

  return {
    ctrlKey,
    shiftKey,
    altKey,
    metaKey,
    key,
  };
}

export function stringToRegExp(str: string) {
  const exceptionCase = [
    '\\',
    '.',
    '*',
    '+',
    '?',
    '|',
    '(',
    ')',
    '[',
    ']',
    '{',
    '}',
    '^',
    '$',
  ];

  return str
    .split('')
    .map((it) => {
      if (exceptionCase.includes(it)) return `\\${it}`;

      return it;
    })
    .join('');
}

export function mergeRegex(arrRegex: string[], type: 'and' | 'or' = 'or') {
  if (arrRegex.length === 0) return '';

  return type === 'and' ? `(${arrRegex.join('')})` : `(${arrRegex.join('|')})`;
}

/**
 * Convert number to time counter
 * @example numberToTimeCounter(3600) => 01:00:00
 */
export function numberToTimeCounter(number: number, showZero = true) {
  number = Math.floor(number); //? remove decimal
  const hours = Math.floor(number / 3600);
  const minutes = Math.floor((number - hours * 3600) / 60);
  const seconds = number - hours * 3600 - minutes * 60;

  const hoursStr = hours < 10 && showZero ? `0${hours}` : `${hours}`;
  const minutesStr = minutes < 10 && showZero ? `0${minutes}` : `${minutes}`;
  const secondsStr = seconds < 10 && showZero ? `0${seconds}` : `${seconds}`;

  if (hours > 0) {
    return `${hoursStr}:${minutesStr}:${secondsStr}`;
  }

  return `${minutesStr}:${secondsStr}`;
}

/**
 * Convert Accept type input to regex for check extension file name
 * @example acceptTypeToRegex('image/png, .jpeg') => /(png|jpeg)$/i
 */
export function acceptTypeToRegex(acceptType: string) {
  if (!acceptType) return new RegExp('');

  const extensions = acceptType.split(', ').map((ext) => ext.trim());

  const regexPattern = extensions
    .map((ext) => {
      if (/\./.test(ext)) {
        return ext.replace(/\./g, '');
      }

      if (/\//.test(ext)) {
        return getExtensionFromMIME(ext as keyof typeof MIME_TO_EXTENSION);
      }

      return null;
    })
    .filter(Boolean)
    .join('|');

  return new RegExp(`(${regexPattern})$`, 'i');
}

/**
 * Convert className to QuerySelector
 * @example stringToSelector('ant-table') => .ant-table
 * @example stringToSelector('shrink basis-[auto] h-[160px]') => .shrink.basis-\\[auto\\].h-\\[160px\\]
 */

export function stringToSelector(str: string) {
  if (!str) return '';

  str = str.replace(/ /g, '.');

  ['[', ']', '(', ')'].forEach((it) => {
    str = str.replace(new RegExp(`\\${it}`, 'g'), `\\${it}`);
  });

  return `.${str}`;
}

export function getExtensionFileName(
  filename: string,
  splitKey: '.' | '/' = '.'
) {
  return filename.split(splitKey).pop().toLowerCase(); // Extract and convert to lowercase
}

export function getFileNameWithoutExtension(fileName: string) {
  const fileNameParts = fileName.split('.');
  fileNameParts.pop();
  return fileNameParts.join('.');
}

export function getMIMEFromFileName(filename: string) {
  const extension = getExtensionFileName(filename);

  for (const mimeType in MIME_TO_EXTENSION) {
    if (MIME_TO_EXTENSION[mimeType] === extension) {
      return mimeType; // Found a matching MIME type
    }
  }

  return null; // Return null if no matching MIME type is found
}

export function getExtensionFromMIME(mime: keyof typeof MIME_TO_EXTENSION) {
  return MIME_TO_EXTENSION[mime] || getExtensionFileName(mime, '/');
}

/* export function htmlToText(html: string): string {
  const tmp = document.createElement('div');

  tmp.innerHTML = html;

  return tmp.textContent || tmp.innerText || '';
}

export const toLuceneQueryString = (queryObject: any, separated = '&', mapKey = '='): string => {
  return join(
    map(queryObject, (value, field) => {
      if (value) {
        return `${field}${mapKey}${value}`;
      }
    }).filter((it) => !isEmpty(it)),
    separated
  );
}; */
